import React, {useEffect, useId, useState} from 'react';
import {Button, Modal, OverlayTrigger, Tooltip} from '@goproblems/ui';
import {isMobile} from 'react-device-detect';
import {toast} from 'react-toastify';
import {useStarProblemMutation} from '@api';
import {useTranslation} from 'react-i18next';

interface ProblemHeaderProps {
    problemId: number;
    canRate: boolean;
    ratedFor: number;
    canNotRateReason: string | null;
    stars: number;
}

type RatingStarIconProps = {
    fill: 'empty' | 'half' | 'full';
};

const STAR_PATH =
    'M12 2.75 14.86 8.54 21.25 9.47 16.63 13.97 17.72 20.33 12 17.32 6.28 20.33 7.37 13.97 2.75 9.47 9.14 8.54 12 2.75Z';

const RatingStarIcon = ({fill}: RatingStarIconProps) => {
    const gradientId = useId();
    const fillValue =
        fill === 'full'
            ? 'currentColor'
            : fill === 'half'
              ? `url(#${gradientId})`
              : 'none';

    return (
        <svg
            aria-hidden="true"
            className="block h-[1em] w-[1em] overflow-visible"
            viewBox="2 2 20 20">
            {fill === 'half' && (
                <defs>
                    <linearGradient
                        id={gradientId}
                        x1="0"
                        x2="1"
                        y1="0"
                        y2="0">
                        <stop offset="50%" stopColor="currentColor" />
                        <stop
                            offset="50%"
                            stopColor="currentColor"
                            stopOpacity="0"
                        />
                    </linearGradient>
                </defs>
            )}
            <path
                d={STAR_PATH}
                fill={fillValue}
                stroke="currentColor"
                strokeLinejoin="round"
                strokeWidth={fill === 'full' ? 1 : 1.35}
            />
        </svg>
    );
};

const StarRating: React.FC<ProblemHeaderProps> = ({
    problemId,
    canRate,
    ratedFor,
    canNotRateReason,
    stars,
}) => {
    const {t} = useTranslation();
    const [rated, setRated] = useState<number>(ratedFor);
    const [hoveredStar, setHoveredStar] = useState<number | null>(null);
    const [showModal, setShowModalModal] = useState<boolean>(false);
    const [starProblem] = useStarProblemMutation();

    const handleClose = () => setShowModalModal(false);
    const handleShow = () => setShowModalModal(true);

    const renderStars = (currentStar: number | null = null) => {
        return Array.from({length: 5}, (_, index) => {
            const isFilled =
                stars >= index + 1 ||
                (currentStar === null && rated >= index + 1) ||
                (currentStar !== null && currentStar >= index + 1);
            const isHalf = stars > index && stars < index + 1;
            const isRated =
                (currentStar == null && rated >= index + 1) ||
                (currentStar !== null && currentStar >= index + 1);

            const fill = isFilled ? 'full' : isHalf ? 'half' : 'empty';
            const colorClass = isRated
                ? 'text-[#f4511e]'
                : fill !== 'empty'
                  ? 'text-[#f59e0b]'
                  : 'text-neutral-400 dark:text-neutral-500';

            return (
                <button
                    type="button"
                    key={`stars-rating-${index}`}
                    className={`inline-flex items-center justify-center rounded-sm border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400 ${colorClass}`}
                    aria-label={t('problem.rate_stars', {count: index + 1})}
                    aria-disabled={!canRate}
                    onMouseOver={() => handleStarHover(index + 1)}
                    onClick={() => handleStarClick(index + 1)}>
                    <RatingStarIcon fill={fill} />
                </button>
            );
        });
    };

    const handleStarHover = (index: number) => {
        if (!canRate) return;
        setHoveredStar(index);
    };

    const handleStarClick = async (index: number) => {
        if (!canRate) return;
        if (isMobile) handleStarUnhover();

        try {
            await starProblem({problemId, star: index}).unwrap();
            setRated(index);
            showNotification('success', 'Success', 'You rated this problem');
        } catch (_error) {
            showNotification('error', 'Error', 'Failed to submit your rating');
        }
    };

    const showNotification = (type: string, title: string, text: string) => {
        toast.info(text, {position: 'top-right', autoClose: 1000});
        window.dispatchEvent(
            new CustomEvent('notification', {
                detail: {
                    type,
                    title,
                    text,
                    dismissible: true,
                    permanent: false,
                },
            })
        );
    };

    const handleStarUnhover = () => {
        if (!canRate) return;
        setHoveredStar(null);
    };

    const handleRenderStarClick = () => {
        if (!canRate) return;
        if (isMobile) {
            handleShow();
        }
    };

    useEffect(() => {
        setRated(ratedFor);
    }, [problemId, ratedFor]);

    const mobileRatingValue = Math.round(stars) || Math.round(rated);
    const mobileRatingColor = rated > 0
        ? 'text-[#f4511e]'
        : stars
          ? 'text-[#f59e0b]'
          : 'text-neutral-400 dark:text-neutral-500';

    const ratingControl = (
        <div
            className={`custom-tooltip-container m-0 inline-flex shrink-0 self-center items-center gap-[2px] leading-none fs-25-px ${canRate ? 'cursor-pointer' : 'cursor-not-allowed'}`}
            onMouseLeave={handleStarUnhover}>
            {isMobile ? (
                <button
                    type="button"
                    onClick={handleRenderStarClick}
                    aria-disabled={!canRate}
                    aria-label={t('problem.rating_value', {
                        rating: mobileRatingValue,
                    })}
                    className="relative inline-flex size-8 items-center justify-center rounded-sm border-0 bg-transparent p-0 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-neutral-400">
                    <span
                        aria-hidden="true"
                        className={`absolute inset-0 inline-flex items-center justify-center text-[1.9rem] ${mobileRatingColor}`}>
                        <RatingStarIcon
                            fill={rated > 0 || stars > 0 ? 'full' : 'empty'}
                        />
                    </span>
                    <span
                        className={`relative z-10 text-xs font-bold leading-none ${rated > 0 || stars > 0 ? 'text-white' : 'text-neutral-700 dark:text-neutral-200'}`}>
                        {mobileRatingValue}
                    </span>
                </button>
            ) : (
                renderStars(hoveredStar)
            )}
        </div>
    );

    return (
        <>
            {!canRate && canNotRateReason ? (
                <OverlayTrigger
                    placement="top"
                    trigger={isMobile ? 'click' : ['hover', 'focus']}
                    rootClose={isMobile}
                    overlay={
                        <Tooltip id="problem-rating-disabled-reason">
                            {canNotRateReason}
                        </Tooltip>
                    }>
                    {ratingControl}
                </OverlayTrigger>
            ) : (
                ratingControl
            )}
            <Modal show={showModal} onHide={handleClose}>
                <Modal.Header closeButton>
                    <Modal.Title>{t('problem.rate')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <span
                        onMouseLeave={handleStarUnhover}
                        className={
                            'd-flex cursor-pointer justify-content-center align-items-center gap-1 font-size-2-rem'
                        }>
                        {renderStars(hoveredStar)}
                    </span>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={handleClose}>
                        {t('common.close')}
                    </Button>
                </Modal.Footer>
            </Modal>
        </>
    );
};

export default StarRating;
